'use client';

import type { AuthUser } from 'src/auth/context/auth-context-value';

import { useRouter } from 'next/navigation';
import { useState, useCallback } from 'react';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';

import { useUser } from 'src/hooks/use-user';

import { track } from 'src/lib/analytics/client';

import { signOut, signInWithDemo } from 'src/auth/context/firebase';

// ----------------------------------------------------------------------

/** Firebase Auth `uid` — 이미 이 계정으로 로그인 중이면 데모 전환 없이 서비스로만 이동 */
const HOME_DEMO_ACCOUNT_UID = 'gqcX1XYwRsgwItSNFpSU9lErAKr1';

function isAlreadyHomeDemoUser(user: AuthUser | null | undefined): boolean {
  return user?.uid === HOME_DEMO_ACCOUNT_UID;
}

// ----------------------------------------------------------------------

export type HomeDemoExploreConfirmDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export function HomeDemoExploreConfirmDialog({
  open,
  onClose,
  onConfirm,
}: HomeDemoExploreConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="home-demo-explore-confirm-title"
      PaperProps={{ sx: { maxWidth: 340, width: '100%' } }}
    >
      <Stack m={4} spacing={2}>
        <Typography variant="h5" id="home-demo-explore-confirm-title">
          미리 써보는 데모 워크스페이스 🙌
        </Typography>
        <Typography variant="body1">
          로그아웃 후 체험이 시작됩니다.
          <br />
          확인하시겠어요?
        </Typography>
        <Stack direction="row" spacing={2} width="100%">
          <Button variant="outlined" color="inherit" fullWidth onClick={onClose}>
            닫기
          </Button>
          <Button variant="contained" fullWidth onClick={onConfirm}>
            데모 확인하기
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}

/**
 * 랜딩「알아보기」: 비로그인 시 즉시 데모 로그인, 로그인 시 확인 다이얼로그 후 진행.
 */
export type HomeDemoExploreOptions = {
  sensorButtonLabel?: string;
  /** 데모 로그인 실행 직전 (예: 모바일 메뉴 닫기) */
  onExecuteStart?: () => void;
};

export function useHomeDemoExplore(options: HomeDemoExploreOptions = {}) {
  const { sensorButtonLabel = '문의하기', onExecuteStart } = options;
  const router = useRouter();
  const user = useUser();
  const [demoLoginLoading, setDemoLoginLoading] = useState(false);
  const [demoConfirmOpen, setDemoConfirmOpen] = useState(false);

  const executeDemoLoginAndTrial = useCallback(async () => {
    if (isAlreadyHomeDemoUser(user)) {
      router.push(paths.service.root);
      return;
    }
    onExecuteStart?.();
    track({ name: 'cta_clicked', type: 'action', props: { cta_id: 'home.demo_explore', label: sensorButtonLabel } }, { uiId: 'marketing-site.home.demo_explore' });
    setDemoLoginLoading(true);
    try {
      if (user) await signOut();
      await signInWithDemo();
      router.push(paths.service.root);
    } catch (error) {
      console.error('데모 로그인 실패:', error);
    } finally {
      setDemoLoginLoading(false);
    }
  }, [user, router, sensorButtonLabel, onExecuteStart]);

  const handleDemoExploreClick = useCallback(() => {
    if (isAlreadyHomeDemoUser(user)) {
      router.push(paths.service.root);
      return;
    }
    if (user) {
      setDemoConfirmOpen(true);
      return;
    }
    executeDemoLoginAndTrial();
  }, [user, router, executeDemoLoginAndTrial]);

  const handleCloseDemoConfirmDialog = useCallback(() => {
    setDemoConfirmOpen(false);
  }, []);

  const handleConfirmDemoFromDialog = useCallback(() => {
    setDemoConfirmOpen(false);
    executeDemoLoginAndTrial();
  }, [executeDemoLoginAndTrial]);

  return {
    demoLoginLoading,
    demoConfirmOpen,
    handleDemoExploreClick,
    handleCloseDemoConfirmDialog,
    handleConfirmDemoFromDialog,
  };
}
